import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Package, Clock, CheckCircle, XCircle, ChevronRight, Search, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import OrderStatusTracker from '../components/OrderStatusTracker';
import CancelOrderModal from '../components/CancelOrderModal';
import { formatISTFull } from '../utils/dateUtils';
import Loading from '../components/Loading';
import { useTenant } from '../context/TenantContext';

export default function Orders() {
    const { tenant } = useTenant();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const toast = useToast();
    const navigate = useNavigate();

    const [trackingOrder, setTrackingOrder] = useState(null);
    const [trackingDetails, setTrackingDetails] = useState(null);
    const [fetchingTracking, setFetchingTracking] = useState(false);
    const [showRawResponse, setShowRawResponse] = useState(false);
    
    const [cancellingOrder, setCancellingOrder] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);

    const handleCancelOrder = async (reason) => {
        setCancelLoading(true);
        try {
            await api.post(`/api/orders/${cancellingOrder.id}/cancel`, { reason });
            toast.success('Order cancelled successfully');
            fetchOrders(false);
            setCancellingOrder(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to cancel order');
        } finally {
            setCancelLoading(false);
        }
    };

    const handleTrackDelivery = async (orderId) => {
        setTrackingOrder(orderId);
        setTrackingDetails(null);
        setFetchingTracking(true);
        try {
            const res = await api.get(`/api/external-delivery/track/${orderId}`);
            setTrackingDetails(res.data?.data || res.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to fetch live tracking details');
        } finally {
            setFetchingTracking(false);
        }
    };

    const [retryLoading, setRetryLoading] = useState({});

    useEffect(() => {
        fetchOrders(true); // Initial fetch (silent / no screen blocking loader)

        // Setup background auto-refresh polling interval (every 10 seconds)
        const intervalId = setInterval(() => {
            fetchOrders(false); // Silent background sync (silent = false loading state)
        }, 10000);

        return () => clearInterval(intervalId);
    }, []);

    const handleRetry = async (orderId) => {
        setRetryLoading(prev => ({ ...prev, [orderId]: true }));
        try {
            const response = await api.post(`/api/payments/retry/${orderId}`);
            const provider = response.data.provider || 'cashfree';

            if (provider === 'razorpay') {
                const rzpOrderId = response.data.orderId;
                const keyId = response.data.keyId;

                const rzpScript = document.createElement('script');
                rzpScript.src = 'https://checkout.razorpay.com/v1/checkout.js';
                rzpScript.async = true;
                document.body.appendChild(rzpScript);

                rzpScript.onload = () => {
                    const options = {
                        key: keyId,
                        amount: response.data.amount,
                        currency: response.data.currency || 'INR',
                        name: 'Savory Bites',
                        description: `Order Retry #${orderId}`,
                        order_id: rzpOrderId,
                        handler: function (rzpRes) {
                            const params = new URLSearchParams({
                                order_id: orderId,
                                razorpay_payment_id: rzpRes.razorpay_payment_id || '',
                                razorpay_order_id: rzpRes.razorpay_order_id || '',
                                razorpay_signature: rzpRes.razorpay_signature || '',
                                status: 'verifying'
                            });
                            window.location.href = `/payment-status?${params.toString()}`;
                        },
                        modal: {
                            ondismiss: function () {
                                setRetryLoading(prev => ({ ...prev, [orderId]: false }));
                            }
                        }
                    };
                    const rzp = new window.Razorpay(options);
                    rzp.open();
                };
            } else if (response.data.paymentSessionId) {
                const paymentSessionId = response.data.paymentSessionId;
                const cfOrderId = response.data.orderId;

                const cashfreeScript = document.createElement('script');
                cashfreeScript.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
                cashfreeScript.async = true;
                document.body.appendChild(cashfreeScript);

                cashfreeScript.onload = () => {
                    const cashfree = window.Cashfree({
                        mode: import.meta.env.VITE_CASHFREE_ENVIRONMENT === 'production' ? 'production' : 'sandbox'
                    });

                    cashfree.checkout({
                        paymentSessionId: paymentSessionId,
                        redirectTarget: '_self',
                        returnUrl: `${window.location.origin}/payment-status?order_id=${orderId}&cf_order_id=${cfOrderId}`
                    });
                };
            }
        } catch (err) {
            console.error('Retry failed:', err);
            toast.error(err.response?.data?.message || 'Failed to re-initiate payment');
            // Refresh orders to sync status if it was already paid/confirmed
            fetchOrders(false);
        } finally {
            setRetryLoading(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const fetchOrders = async (showMainLoader = false) => {
        try {
            if (showMainLoader) {
                setLoading(true);
            }
            const response = await api.get('/api/orders/my-orders');
            console.log('Orders fetched:', response.data);
            setOrders(response.data || []);
        } catch (err) {
            console.error('Error fetching orders:', err);
            if (showMainLoader) {
                setError('Failed to load orders');
            }
        } finally {
            if (showMainLoader) {
                setLoading(false);
            }
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
            case 'completed':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'cancelled':
                return <XCircle className="w-5 h-5 text-red-500" />;
            case 'pending':
                return <Clock className="w-5 h-5 text-yellow-500" />;
            default:
                return <Package className="w-5 h-5 text-blue-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-blue-100 text-blue-800';
        }
    };

    if (loading) {
        return <Loading mode="page" message="Fetching your order history..." />;
    }

    if (error) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-16 text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-600">{error}</h2>
                <button onClick={fetchOrders} className="btn-primary mt-4">
                    Retry
                </button>
            </div>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-16 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">No orders yet</h2>
                <p className="text-gray-600 mb-6">Start ordering delicious food!</p>
                <button onClick={() => navigate('/menu')} className="btn-primary">
                    Browse Menu
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 pb-24 md:pb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold">My Orders</h1>
                    <button 
                        onClick={() => fetchOrders(true)} 
                        className="p-2 text-gray-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Refresh status"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by order ID or address..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                </div>
            </div>

            <div className="space-y-4">
                {(() => {
                    const filtered = orders.filter(o => {
                        if (!searchTerm) return true;
                        const s = searchTerm.toLowerCase();
                        return (o.id?.toLowerCase().includes(s) || o.delivery_address?.toLowerCase().includes(s));
                    });
                    const totalPages = Math.ceil(filtered.length / itemsPerPage);
                    const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                    return (<>
                    {paginated.map((order) => (
                    <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                            <div>
                                <p className="text-sm text-gray-600">Order #{order.id}</p>
                                <p className="text-xs text-gray-500">
                                    {formatISTFull(order.created_at)}
                                </p>
                            </div>
                            <div className="flex items-center space-x-2">
                                {order.delivery_status && order.delivery_status !== 'unassigned' && order.status !== 'delivered' && (
                                    <div className={`px-3 py-1 rounded-full text-sm font-medium border ${document.location.pathname ? 'bg-indigo-50 text-indigo-800 border-indigo-200' : ''
                                        }`}>
                                        Del: {order.delivery_status.replace('_', ' ').toUpperCase()}
                                    </div>
                                )}
                                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                                    {order.status || 'Processing'}
                                </div>
                                {(order.status === 'payment_failed' || (order.status === 'pending' && order.payment_status !== 'paid')) && (
                                    <button
                                        onClick={() => handleRetry(order.id)}
                                        disabled={retryLoading[order.id]}
                                        className="px-3 py-1 bg-primary-600 text-white rounded-full text-xs font-bold hover:bg-primary-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                                    >
                                        {retryLoading[order.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                        {retryLoading[order.id] ? '...' : 'RETRY'}
                                    </button>
                                )}
                                {(order.status === 'pending' || order.status === 'confirmed') && (
                                    <button
                                        onClick={() => setCancellingOrder(order)}
                                        className="px-3.5 py-1.5 border border-red-200 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200 flex items-center gap-1 shadow-sm"
                                    >
                                        <XCircle className="w-3.5 h-3.5" />
                                        Cancel Order
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="p-4">
                            {/* Order Status Tracker */}
                            {order.status !== 'cancelled' && order.status !== 'pending' && (
                                <div className="mb-6 border-b pb-6">
                                    <OrderStatusTracker order={order} />
                                </div>
                            )}

                            {/* Order Items */}
                            <div className="space-y-2 mb-4">

                                {Array.isArray(order.items) && order.items.map((item, idx) => (
                                    item && item.name ? (
                                        <div key={idx} className="flex justify-between text-sm">
                                            <span>{item.name} × {item.quantity || 1}</span>
                                            <span>₹{((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                                        </div>
                                    ) : null
                                ))}
                            </div>

                            <div className="border-t pt-3 flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                    {getStatusIcon(order.status)}
                                    <span className="text-sm text-gray-600">
                                        {order.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <p className="text-lg font-bold">₹{parseFloat(order.total_amount).toFixed(2)}</p>
                                </div>
                            </div>

                            {order.delivery_address && (
                                <div className="mt-3 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                                    <p className="font-medium">Delivery Address:</p>
                                    <p>{order.delivery_address}</p>
                                    {order.delivery_type === 'external' ? (
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                                                🏍️ {order.external_provider === 'adloggs' ? 'Adloggs Delivery' : order.external_provider}
                                                {order.external_courier_name && ` • ${order.external_courier_name}`}
                                            </span>
                                            <div className="flex flex-col gap-1.5 mt-1">
                                                {order.external_tracking_url && (
                                                    <a
                                                        href={order.external_tracking_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-purple-600 hover:underline font-bold text-left w-fit"
                                                    >
                                                        Track Delivery ↗
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => handleTrackDelivery(order.id)}
                                                    className="text-xs text-gray-500 hover:underline text-left w-fit font-medium flex items-center gap-0.5"
                                                >
                                                    Check Live Status 🔄
                                                </button>
                                            </div>
                                        </div>
                                    ) : order.delivery_partner_id ? (
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                                                🏍️ {order.partner_name || 'Delivery Partner'}
                                                {order.partner_phone && ` • ${order.partner_phone}`}
                                            </span>
                                            <span className="text-xs text-gray-500 capitalize">
                                                {order.delivery_status ? order.delivery_status.replace(/_/g, ' ') : 'Assigned'}
                                            </span>
                                            {order.delivery_status && order.delivery_status !== 'delivered' && order.delivery_status !== 'cancelled' && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse uppercase tracking-wider">
                                                    In Progress
                                                </span>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {order.delivery_otp && (
                                <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-lg flex justify-between items-center">
                                    <div className="pr-4">
                                        <p className="font-semibold flex items-center gap-1.5">
                                            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                                            Delivery Verification OTP:
                                        </p>
                                        <p className="text-xs text-amber-600 mt-0.5">Share this code with the delivery partner only after receiving your items.</p>
                                    </div>
                                    <span className="text-2xl font-black tracking-widest font-mono text-amber-900 bg-white border border-amber-200 px-3 py-1.5 rounded-md shadow-sm">
                                        {order.delivery_otp}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                ))
                }
                {filtered.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No orders matching your search.</div>
                )}
                {totalPages > 1 && (
                    <div className="flex justify-between items-center py-4">
                        <div className="text-sm text-gray-500">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
                        </div>
                        <div className="flex space-x-2">
                            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-50 hover:bg-gray-100">Previous</button>
                            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-50 hover:bg-gray-100">Next</button>
                        </div>
                    </div>
                )}
                </>); })()
                }
            </div>

            {/* Live Tracking Modal */}
            {trackingOrder && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative overflow-hidden text-left max-h-[90vh] flex flex-col">
                        <button
                            onClick={() => {
                                setTrackingOrder(null);
                                setTrackingDetails(null);
                                setShowRawResponse(false);
                            }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 font-bold text-xl"
                        >
                            ✕
                        </button>
                        
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex-shrink-0">
                            Live Delivery Tracking
                        </h3>

                        <div className="overflow-y-auto flex-grow pr-1 space-y-4">
                            {fetchingTracking ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                                    <p className="text-sm text-gray-500">Contacting Adloggs network for live update...</p>
                                </div>
                            ) : trackingDetails ? (
                                <>
                                    <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                                        <div className="text-xs text-indigo-500 font-semibold uppercase tracking-wider">Status</div>
                                        <div className="text-base font-bold text-indigo-900 mt-1 capitalize">
                                            {(() => {
                                                const statusId = trackingDetails.order_status_id;
                                                const desc = trackingDetails.order_status_description;
                                                if (desc) return desc;
                                                const map = {
                                                    2: 'Pending (Finding Rider)',
                                                    3: 'Assigned',
                                                    11: 'Arrived at Pickup',
                                                    4: 'Picked Up',
                                                    9: 'Out For Delivery',
                                                    8: 'Arrived',
                                                    5: 'Delivered',
                                                    6: 'Cancelled',
                                                    14: 'Cancelled'
                                                };
                                                return map[statusId] || `Status Code: ${statusId}`;
                                            })()}
                                        </div>
                                    </div>

                                    {trackingDetails.deliveryStaffDetails?.name && (
                                        <div className="border-t pt-3">
                                            <div className="text-xs text-gray-500">Rider Partner</div>
                                            <div className="font-bold text-gray-900 mt-0.5">{trackingDetails.deliveryStaffDetails.name}</div>
                                            {trackingDetails.deliveryStaffDetails.phone && (
                                                <a 
                                                    href={`tel:${trackingDetails.deliveryStaffDetails.phone}`}
                                                    className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1 font-medium"
                                                >
                                                    📞 Call: {trackingDetails.deliveryStaffDetails.phone}
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    {trackingDetails.eta && (
                                        <div className="border-t pt-3 grid grid-cols-2 gap-4">
                                            {trackingDetails.eta.to_pickup !== undefined && (
                                                <div>
                                                    <div className="text-xs text-gray-500">Pickup ETA</div>
                                                    <div className="font-semibold text-gray-800 mt-0.5">{trackingDetails.eta.to_pickup} mins</div>
                                                </div>
                                            )}
                                            {trackingDetails.eta.to_delivery !== undefined && (
                                                <div>
                                                    <div className="text-xs text-gray-500">Delivery ETA</div>
                                                    <div className="font-semibold text-gray-800 mt-0.5">{trackingDetails.eta.to_delivery} mins</div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {trackingDetails.deliveryStaffDetails?.currentLocation?.lat && (
                                        <div className="border-t pt-3">
                                            <div className="text-xs text-gray-500 mb-1">Rider Location</div>
                                            <a 
                                                href={`https://www.google.com/maps/search/?api=1&query=${trackingDetails.deliveryStaffDetails.currentLocation.lat},${trackingDetails.deliveryStaffDetails.currentLocation.long}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg font-bold border border-emerald-200 inline-block hover:bg-emerald-100"
                                            >
                                                📍 View Live on Google Maps
                                            </a>
                                        </div>
                                    )}

                                    {/* Removed Raw API Response block */}
                                </>
                            ) : (
                                <p className="text-sm text-gray-500 text-center py-4">No live tracking information available yet.</p>
                            )}
                        </div>

                        <div className="border-t pt-4 mt-4 flex justify-end gap-2 flex-shrink-0">
                            {trackingDetails && (
                                <button
                                    onClick={() => handleTrackDelivery(trackingOrder)}
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-all"
                                >
                                    Refresh Status
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setTrackingOrder(null);
                                    setTrackingDetails(null);
                                    setShowRawResponse(false);
                                }}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Order Modal */}
            <CancelOrderModal
                isOpen={!!cancellingOrder}
                orderId={cancellingOrder?.id}
                isPaid={cancellingOrder?.payment_status === 'paid'}
                onConfirm={handleCancelOrder}
                onCancel={() => setCancellingOrder(null)}
            />
        </div>
    );
}